import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from 'src/app/_services/auth.service';
import { AlertService } from 'src/app/_services/alert.service';
import { AUTH_CONSTANTS } from 'src/app/_helpers/auth.constants';
import { HttpResponse } from '@angular/common/http';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css'
})
export class ChangePasswordComponent implements OnInit {
  changePasswordForm: FormGroup;
  isLoading: boolean = false; 
  
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(AUTH_CONSTANTS.MIN_PASSWORD_LENGTH)]],
      confirmPassword: ['', [Validators.required, Validators.minLength(AUTH_CONSTANTS.MIN_PASSWORD_LENGTH)]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit(): void {}

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  get currentPassword() {
    return this.changePasswordForm.get('currentPassword');
  }

  get newPassword() {
    return this.changePasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.changePasswordForm.get('confirmPassword');
  }

  toggleCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  private showSuccessAlert(): void {
    this.alertService.customInfoPopup({
      icon: 'assets/img/Tick.svg',
      title: "Password Changed Successfully",
      description: 'Your password has been changed successfully.',
      primaryButton: {
        text: 'Done',
        action: () => {
          this.router.navigate(['/profile']);
          this.changePasswordForm.reset();
          localStorage.clear();
          this.authService.logout();
        }
      }
    });
  }

  private showErrorAlert(errorMessage: string, status?: number): void {
    const title = status === 400 ? "Invalid Password" : "Failed to change password";
    this.alertService.customInfoPopup({
      icon: 'assets/img/Info.svg',
      title: title,
      description: errorMessage,
      primaryButton: {
        text: 'Done',
      }
    });
  }

  onSubmit(): void {
    if (this.changePasswordForm.valid) {
      this.isLoading = true;

      this.authService.changePassword(
        this.changePasswordForm.value.currentPassword,
        this.changePasswordForm.value.newPassword
      ).subscribe({
        next: (response: HttpResponse<any>) => {
          this.isLoading = false;
          const responseStatus = response.body?.response?.status;
          const responseMessage = response.body?.response?.message;
         if (responseStatus === 400) {
            this.showErrorAlert(responseMessage || 'Invalid password requirements.', 400);
          } 
          else {
            this.showSuccessAlert();
          }
        },
        error: (err: any) => {
          this.isLoading = false;
          const errorStatus = err?.error?.response?.status;
          const errorMessage = err?.error?.response?.message || err?.error?.message || 'Failed to change password. Please try again.';
          this.showErrorAlert(errorMessage, errorStatus);
        }
      });
    } else {
      this.changePasswordForm.markAllAsTouched();
    }
  }

  onClose(): void {
    this.router.navigate(['/profile']);
  }
}